import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Box, Alert, Divider } from "@mui/material";
import PeopleIcon             from "@mui/icons-material/People";
import BlockIcon              from "@mui/icons-material/Block";
import TrendingUpIcon         from "@mui/icons-material/TrendingUp";
import ForumIcon              from "@mui/icons-material/Forum";
import ChatBubbleOutlineIcon  from "@mui/icons-material/ChatBubbleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HowToRegIcon           from "@mui/icons-material/HowToReg";

import { getAdminSummary, getAdminUserMetrics, getAdminLoginActivity, getAdminUserChatHistory, getAdminExportChatHistory, getAdminExportLoginLog } from "../../services/apiService";
import { POPPINS } from "./adminConstants";
import SummaryCard          from "./SummaryCard";
import UserMetricsFilters   from "./UserMetricsFilters";
import UserMetricsTable     from "./UserMetricsTable";
import LoginLogDrawer       from "./LoginLogDrawer";
import ChatHistoryDrawer    from "./ChatHistoryDrawer";
import ExportDateRangeDialog from "./ExportDateRangeDialog";

const DRAWER_RPP      = 20;
const CHAT_DRAWER_RPP = 25;

const DashboardTab = () => {
  // ── Summary ──────────────────────────────────────────────────────
  const [summary, setSummary]               = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError]     = useState(null);

  // ── User metrics table ───────────────────────────────────────────
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const [search, setSearch]                 = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [page, setPage]                     = useState(0);
  const [rpp, setRpp]                       = useState(25);
  const [sortCol, setSortCol]               = useState(null);
  const [sortDir, setSortDir]               = useState("asc");
  const [exporting, setExporting]           = useState(false);

  // ── Login log drawer ─────────────────────────────────────────────
  const [drawerUser, setDrawerUser]       = useState(null);
  const [drawerRows, setDrawerRows]       = useState([]);
  const [drawerTotal, setDrawerTotal]     = useState(0);
  const [drawerPage, setDrawerPage]       = useState(0);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError]     = useState(null);

  // ── Chat history drawer ──────────────────────────────────────────
  const [chatDrawerUser, setChatDrawerUser]       = useState(null);
  const [chatDrawerRows, setChatDrawerRows]       = useState([]);
  const [chatDrawerTotal, setChatDrawerTotal]     = useState(0);
  const [chatDrawerPage, setChatDrawerPage]       = useState(0);
  const [chatDrawerLoading, setChatDrawerLoading] = useState(false);
  const [chatDrawerError, setChatDrawerError]     = useState(null);

  // ── Datasource filter (from DatasourceBreakdown) ─────────────────
  const [datasourceFilter, setDatasourceFilter]   = useState(null);

  // ── Export dialog ─────────────────────────────────────────────────
  const [exportDialog, setExportDialog]   = useState(null); // null | 'chat' | 'login'
  const [exportingChat, setExportingChat] = useState(false);
  const [exportingLogin, setExportingLogin] = useState(false);

  // ── Fetch helpers ────────────────────────────────────────────────
  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true); setSummaryError(null);
    try {
      const res = await getAdminSummary();
      if (res?.error) { setSummaryError(res.error); return; }
      setSummary(res.data ?? null);
    } catch { setSummaryError("Failed to load summary."); }
    finally   { setSummaryLoading(false); }
  }, []);

  const fetchMetrics = useCallback(async (pg, rpp_, q, active, d) => {
    setLoading(true); setError(null);
    try {
      const res = await getAdminUserMetrics({ search: q, is_active: active, days: d, limit: rpp_, offset: pg * rpp_ });
      if (res?.error) { setError(res.error); return; }
      setRows(res.data ?? []); setTotal(res.total ?? 0);
    } catch { setError("Failed to load user metrics."); }
    finally   { setLoading(false); }
  }, []);

  const fetchDrawerLogs = useCallback(async (email, pg) => {
    setDrawerLoading(true); setDrawerError(null);
    try {
      const res = await getAdminLoginActivity({ search: email, limit: DRAWER_RPP, offset: pg * DRAWER_RPP });
      if (res?.error) { setDrawerError(res.error); return; }
      setDrawerRows(res.data ?? []); setDrawerTotal(res.total ?? 0);
    } catch { setDrawerError("Failed to load login logs."); }
    finally   { setDrawerLoading(false); }
  }, []);

  const fetchChatHistory = useCallback(async (userId, pg, dsFilter) => {
    setChatDrawerLoading(true); setChatDrawerError(null);
    try {
      const res = await getAdminUserChatHistory({ user_id: userId, limit: CHAT_DRAWER_RPP, offset: pg * CHAT_DRAWER_RPP, datasource: dsFilter || undefined });
      if (res?.error) { setChatDrawerError(res.error); return; }
      setChatDrawerRows(res.data ?? []); setChatDrawerTotal(res.total ?? 0);
    } catch { setChatDrawerError("Failed to load chat history."); }
    finally   { setChatDrawerLoading(false); }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchMetrics(0, 25, "", "", 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Login drawer handlers ────────────────────────────────────────
  const openLoginDrawer = (row) => {
    const name = [row.first_name, row.last_name].filter(Boolean).join(" ") || row.email_id;
    setDrawerUser({ email_id: row.email_id, name });
    setDrawerPage(0); setDrawerRows([]); setDrawerTotal(0);
    fetchDrawerLogs(row.email_id, 0);
  };
  const closeLoginDrawer   = () => { setDrawerUser(null); setDrawerRows([]); };
  const handleDrawerPage   = (_, p) => { setDrawerPage(p); fetchDrawerLogs(drawerUser.email_id, p); };

  // ── Chat drawer handlers ─────────────────────────────────────────
  const openChatDrawer = (row) => {
    const name = [row.first_name, row.last_name].filter(Boolean).join(" ") || row.email_id;
    setChatDrawerUser({ user_id: String(row.user_id), name, email_id: row.email_id, chatCount: row.total_messages ?? 0 });
    setChatDrawerPage(0); setChatDrawerRows([]); setChatDrawerTotal(0);
    fetchChatHistory(String(row.user_id), 0, datasourceFilter);
  };
  const closeChatDrawer    = () => { setChatDrawerUser(null); setChatDrawerRows([]); setDatasourceFilter(null); };
  const handleChatDrawerPage = (_, p) => { setChatDrawerPage(p); fetchChatHistory(chatDrawerUser.user_id, p, datasourceFilter); };
  const handleDatasourceChange = useCallback((newDs) => {
    setDatasourceFilter(newDs);
    setChatDrawerPage(0);
    if (chatDrawerUser) fetchChatHistory(chatDrawerUser.user_id, 0, newDs);
  }, [chatDrawerUser, fetchChatHistory]);

  // ── Filter / sort / pagination handlers ─────────────────────────
  const handleSearchChange = (e)    => { const q = e.target.value; setSearch(q); setPage(0); fetchMetrics(0, rpp, q, isActiveFilter, 0); };
  const handleClearSearch  = ()     => { setSearch(""); setPage(0); fetchMetrics(0, rpp, "", isActiveFilter, 0); };
  const handleStatus       = (e)    => { const v = e.target.value; setIsActiveFilter(v); setPage(0); fetchMetrics(0, rpp, search, v, 0); };
  const handlePage         = (_, p) => { setPage(p); fetchMetrics(p, rpp, search, isActiveFilter, 0); };
  const handleRpp          = (e)    => { const r = parseInt(e.target.value, 10); setRpp(r); setPage(0); fetchMetrics(0, r, search, isActiveFilter, 0); };
  const handleSort         = (col)  => { const asc = sortCol === col && sortDir === "asc"; setSortDir(asc ? "desc" : "asc"); setSortCol(col); };

  const sortedRows = useMemo(() => {
    if (!sortCol) return rows;
    return [...rows].sort((a, b) => {
      const av = a[sortCol] ?? "", bv = b[sortCol] ?? "";
      if (av < bv) return sortDir === "asc" ? -1 :  1;
      if (av > bv) return sortDir === "asc" ?  1 : -1;
      return 0;
    });
  }, [rows, sortCol, sortDir]);

  // ── CSV export helpers ────────────────────────────────────────────
  const triggerDownload = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportChatConfirm = async (startDate, endDate) => {
    setExportingChat(true);
    try {
      const res = await getAdminExportChatHistory({ start_date: startDate, end_date: endDate });
      const all = res?.data ?? [];
      const headers = ["First Name", "Last Name", "Email", "Group Name", "Question", "Datasource", "Timestamp"];
      const csvRows = all.map((r) => [
        `"${r.first_name || ""}"`,
        `"${r.last_name  || ""}"`,
        `"${r.email_id   || ""}"`,
        `"${(r.group_name || "").replace(/"/g, '""')}"`,
        `"${(r.question  || "").replace(/"/g, '""')}"`,
        `"${r.datasource || ""}"`,
        `"${r.timestamp  || ""}"`,
      ].join(","));
      triggerDownload([headers.join(","), ...csvRows].join("\n"), `chat_history_${startDate}_${endDate}.csv`);
    } catch { /* silent */ }
    finally { setExportingChat(false); setExportDialog(null); }
  };

  const handleExportLoginConfirm = async (startDate, endDate) => {
    setExportingLogin(true);
    try {
      const res = await getAdminExportLoginLog({ start_date: startDate, end_date: endDate });
      const all = res?.data ?? [];
      const headers = ["First Name", "Last Name", "Email", "Login Timestamp", "IP Address", "Status", "Failure Reason"];
      const csvRows = all.map((r) => [
        `"${r.first_name       || ""}"`,
        `"${r.last_name        || ""}"`,
        `"${r.email_id         || ""}"`,
        `"${r.login_timestamp  || ""}"`,
        `"${r.ip_address       || ""}"`,
        `"${r.login_status     || ""}"`,
        `"${r.failure_reason   || ""}"`,
      ].join(","));
      triggerDownload([headers.join(","), ...csvRows].join("\n"), `login_log_${startDate}_${endDate}.csv`);
    } catch { /* silent */ }
    finally { setExportingLogin(false); setExportDialog(null); }
  };

  // ── CSV export (users) ───────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await getAdminUserMetrics({ search, is_active: isActiveFilter, days: 0, limit: 10000, offset: 0 });
      const all = res?.data ?? [];
      const headers = ["Name","Email","Status","Created","Last Login","Inactive Days","Total Logins","Failed Logins","Chat Groups","Messages","Last Chat"];
      const csvRows = all.map((r) => [
        `"${[r.first_name, r.last_name].filter(Boolean).join(" ")}"`,
        `"${r.email_id || ""}"`,
        r.soft_delete ? "Deleted" : r.is_active ? "Active" : "Inactive",
        r.created_at ? new Date(r.created_at).toLocaleDateString() : "",
        r.last_login ? new Date(r.last_login).toLocaleDateString() : "",
        r.days_since_last_login ?? "",
        r.total_logins  ?? 0,
        r.failed_logins ?? 0,
        r.total_groups  ?? 0,
        r.total_messages ?? 0,
        r.last_chat ? new Date(r.last_chat).toLocaleDateString() : "",
      ].join(","));
      const csv  = [headers.join(","), ...csvRows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `user_metrics_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
    finally { setExporting(false); }
  };

  // ── Render ───────────────────────────────────────────────────────
  return (
    <Box>
      {/* Summary tiles */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, px: 3, pt: 2.5, pb: 2 }}>
        <SummaryCard icon={<PeopleIcon fontSize="small" />}             label="Total Users"       value={summary?.total_users}       color="#1976d2" loading={summaryLoading} />
        <SummaryCard icon={<CheckCircleOutlineIcon fontSize="small" />} label="Active Users"       value={summary?.active_users}       color="#2e7d32" loading={summaryLoading} />
        <SummaryCard icon={<BlockIcon fontSize="small" />}              label="Inactive / Deleted" value={summary?.inactive_users}     color="#c62828" loading={summaryLoading} />
        <SummaryCard icon={<HowToRegIcon fontSize="small" />}           label="Logins Today"       value={summary?.logins_today}       color="#6a1b9a" loading={summaryLoading} />
        <SummaryCard icon={<TrendingUpIcon fontSize="small" />}         label="Logins This Week"   value={summary?.logins_this_week}   color="#0277bd" loading={summaryLoading} />
        <SummaryCard icon={<ForumIcon fontSize="small" />}              label="Total Chat Groups"  value={summary?.total_chat_groups}  color="#00838f" loading={summaryLoading} />
        <SummaryCard icon={<ChatBubbleOutlineIcon fontSize="small" />}  label="Total Messages"     value={summary?.total_messages}     color="#558b2f" loading={summaryLoading} />
      </Box>

      <Divider />

      {/* Filters */}
      <UserMetricsFilters
        search={search}
        isActiveFilter={isActiveFilter}
        exporting={exporting}
        exportingChat={exportingChat}
        exportingLogin={exportingLogin}
        onSearchChange={handleSearchChange}
        onClearSearch={handleClearSearch}
        onStatusChange={handleStatus}
        onExport={handleExport}
        onExportChat={() => setExportDialog("chat")}
        onExportLogin={() => setExportDialog("login")}
      />

      {summaryError && <Alert severity="error" sx={{ mx: 3, mb: 1, ...POPPINS }}>{summaryError}</Alert>}
      {error        && <Alert severity="error" sx={{ mx: 3, mb: 1, ...POPPINS }}>{error}</Alert>}

      {/* Table */}
      <UserMetricsTable
        rows={sortedRows}
        total={total}
        loading={loading}
        page={page}
        rpp={rpp}
        sortCol={sortCol}
        sortDir={sortDir}
        onSort={handleSort}
        onPage={handlePage}
        onRpp={handleRpp}
        onLoginDrawerOpen={openLoginDrawer}
        onChatDrawerOpen={openChatDrawer}
      />

      {/* Drawers */}
      <LoginLogDrawer
        drawerUser={drawerUser}
        drawerRows={drawerRows}
        drawerTotal={drawerTotal}
        drawerPage={drawerPage}
        drawerLoading={drawerLoading}
        drawerError={drawerError}
        onClose={closeLoginDrawer}
        onPageChange={handleDrawerPage}
      />

      <ChatHistoryDrawer
        chatDrawerUser={chatDrawerUser}
        chatDrawerRows={chatDrawerRows}
        chatDrawerTotal={chatDrawerTotal}
        chatDrawerPage={chatDrawerPage}
        chatDrawerLoading={chatDrawerLoading}
        chatDrawerError={chatDrawerError}
        datasourceFilter={datasourceFilter}
        onDatasourceChange={handleDatasourceChange}
        onClose={closeChatDrawer}
        onPageChange={handleChatDrawerPage}
      />

      {/* Export date-range dialogs */}
      <ExportDateRangeDialog
        open={exportDialog === "chat"}
        title="Export Chat History CSV"
        loading={exportingChat}
        onClose={() => setExportDialog(null)}
        onExport={handleExportChatConfirm}
      />
      <ExportDateRangeDialog
        open={exportDialog === "login"}
        title="Export Login Log CSV"
        loading={exportingLogin}
        onClose={() => setExportDialog(null)}
        onExport={handleExportLoginConfirm}
      />
    </Box>
  );
};

export default DashboardTab;
